/**
 * Generic CRUD collection hook factory — DRY-up the five near-identical
 * fetchX/createX/updateX/deleteX domain hooks that live in WeddingContext.
 *
 * Usage:
 *   const events = useCrudCollection({ basePath: 'events', byWedding: true });
 *   events.items            // state
 *   events.fetch(weddingId) // GET /events/{weddingId}
 *   events.create(payload)  // POST /events
 *   events.update(id, p)    // PUT /events/{id}
 *   events.remove(id)       // DELETE /events/{id}
 */
import { useCallback, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useCrudCollection({ basePath, buildListUrl }) {
  const [items, setItems] = useState([]);

  const fetch = useCallback(async (weddingId, extra) => {
    if (!weddingId) return [];
    try {
      const url = buildListUrl
        ? buildListUrl(weddingId, extra)
        : `${API}/${basePath}/${weddingId}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setItems(data);
      return data;
    } catch (err) {
      console.error(`Error fetching ${basePath}:`, err);
      return [];
    }
  }, [basePath, buildListUrl]);

  const create = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/${basePath}`, payload, { withCredentials: true });
    setItems((prev) => [...prev, data]);
    return data;
  }, [basePath]);

  const update = useCallback(async (id, payload) => {
    const { data } = await axios.put(`${API}/${basePath}/${id}`, payload, { withCredentials: true });
    setItems((prev) => prev.map((x) => (x.id === id ? data : x)));
    return data;
  }, [basePath]);

  const remove = useCallback(async (id) => {
    await axios.delete(`${API}/${basePath}/${id}`, { withCredentials: true });
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, [basePath]);

  return { items, setItems, fetch, create, update, remove };
}
